import { download, generateCsv, mkConfig } from 'export-to-csv';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Download } from '@/components/icons/barrel';
import { Button } from '@/components/ui';
import { getOrderValueFromPrice } from '@/features/trade/order/utils/math'; // eslint-disable-line import/no-restricted-paths
import { privateV2Api } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  getAllMarketStoreStates,
} from '@/store/use-markets-store';
import { FilledOrder, useOrdersStore } from '@/store/use-orders-store';
import { OrderHistoryFilterType, OrderStatus } from '@/types';
import { abbrFromSym } from '@/utils/token-symbol';
import { bigIntToNum, formatOrderStatus } from '@/utils/value-format';

const FILTER_TO_API_STATUS: Record<
  OrderHistoryFilterType,
  string[] | undefined
> = {
  [OrderHistoryFilterType.ALL]: undefined,
  [OrderHistoryFilterType.FILLED]: [
    OrderStatus.FILLED,
    OrderStatus.PARTIALLY_FILLED,
  ],
  [OrderHistoryFilterType.REJECTED]: [OrderStatus.REJECTED],
  [OrderHistoryFilterType.CANCELLED]: [OrderStatus.CANCELLED],
};

const FILTER_TO_STORE_STATUSES: Record<
  OrderHistoryFilterType,
  Set<OrderStatus> | undefined
> = {
  [OrderHistoryFilterType.ALL]: undefined,
  [OrderHistoryFilterType.FILLED]: new Set([
    OrderStatus.FILLED,
    OrderStatus.PARTIALLY_FILLED,
  ]),
  [OrderHistoryFilterType.REJECTED]: new Set([OrderStatus.REJECTED]),
  [OrderHistoryFilterType.CANCELLED]: new Set([OrderStatus.CANCELLED]),
};

const downloadBlob = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vest-history-report-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const exportFromApi = (filter: OrderHistoryFilterType): Promise<void> => {
  return privateV2Api
    .get<Blob>('/v2/orders/export', {
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
      params: { status: FILTER_TO_API_STATUS[filter] },
      paramsSerializer: { indexes: null },
    })
    .then(downloadBlob);
};

function exportFromStore(
  filter: OrderHistoryFilterType,
  t: (key: string) => string,
) {
  const { address } = useAccountStore.getState();
  const { filledOrderIds, cancelledOrderIds, rejectedOrderIds, orders } =
    useOrdersStore.getState().getOrders(address);
  const ids = filledOrderIds.union(rejectedOrderIds).union(cancelledOrderIds);

  if (ids.size === 0) {
    throw new Error('No data to export');
  }

  const markets = getAllMarketStoreStates();
  const allowedStatuses = FILTER_TO_STORE_STATUSES[filter];

  const data = Array.from(ids, (id) => orders[id])
    .filter(
      (order) =>
        order.symbol in markets &&
        (!allowedStatuses || allowedStatuses.has(order.status)),
    )
    .map((order) => {
      const { marketSpec } = markets[order.symbol];
      const { priceDecimals, sizeDecimals } = marketSpec;
      const isFilled =
        order.status === OrderStatus.FILLED ||
        order.status === OrderStatus.PARTIALLY_FILLED;

      return {
        [t('exportOrdersHistory.id')]: order.id,
        [t('exportOrdersHistory.time')]: order.postTime,
        [t('exportOrdersHistory.market')]: abbrFromSym(order.symbol),
        [t('exportOrdersHistory.side')]: order.isBuy
          ? t('exportOrdersHistory.long')
          : t('exportOrdersHistory.short'),
        [t('exportOrdersHistory.size')]: bigIntToNum(order.size, sizeDecimals),
        [t('exportOrdersHistory.value')]:
          getOrderValueFromPrice(
            order.symbol,
            isFilled ? order.lastFilledSize : order.size,
            isFilled ? order.lastFilledPrice : order.limitPrice,
          )?.decimal ?? '-',
        [t('exportOrdersHistory.fillPrice')]: isFilled
          ? bigIntToNum((order as FilledOrder).lastFilledPrice, priceDecimals)
          : '-',
        [t('exportOrdersHistory.funding')]: isFilled
          ? bigIntToNum(order.settledFunding, COLLATERAL_DECIMALS)
          : '-',
        [t('exportOrdersHistory.fee')]: isFilled
          ? bigIntToNum(order.fees, COLLATERAL_DECIMALS)
          : '-',
        [t('exportOrdersHistory.pnl')]: isFilled
          ? bigIntToNum(order.realizedPnl, COLLATERAL_DECIMALS)
          : '-',
        [t('exportOrdersHistory.status')]: formatOrderStatus(order.status, t),
      };
    });

  if (data.length === 0) {
    throw new Error('No data to export');
  }

  const config = mkConfig({
    useKeysAsHeaders: true,
    filename: `vest-history-report-${Date.now()}`,
  });
  const csv = generateCsv(config)(data);

  download(config)(csv);
}

type Props = {
  filter: OrderHistoryFilterType;
};

export default function ExportOrdersHistory({ filter }: Props) {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const address = useAccountStore((state) => state.address);
  const hasOrders = useOrdersStore((state) => {
    const { filledOrderIds, cancelledOrderIds, rejectedOrderIds } =
      state.getOrders(address);

    return (
      filledOrderIds.size > 0 ||
      cancelledOrderIds.size > 0 ||
      rejectedOrderIds.size > 0
    );
  });

  const onExport = async () => {
    setIsLoading(true);

    try {
      await exportFromApi(filter);
    } catch {
      try {
        exportFromStore(filter, t);
      } catch {
        toast.error(t('exportOrdersHistory.exportError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasOrders) {
    return;
  }

  return (
    <Button variant="outline2" disabled={isLoading} onClick={onExport}>
      <Download size={14} className="relative bottom-px" />
      {t('exportOrdersHistory.exportCsv')}
    </Button>
  );
}
